import { useState } from 'react'
import { Pin, PinOff, Pencil, Trash2, Check, Plus } from 'lucide-react'
import { apiGet, postJson } from '../../lib/api.js'
import { apiErrorText } from '../../lib/apiError.js'
import { nameInitial } from '../../lib/text.js'
import { patientNotes, noteId, noteAuthor, canEditNote, canUnpinNote, noteEdited, mergeNote } from '../../lib/patientNotes.js'
import {
  Button, Checkbox, ConfirmDialog, CountBadge, EmptyState, ErrorState, IconButton, Loading, Textarea,
  fmtDate, fmtDateTime, useToast,
} from '../../kit'

// โน้ตผู้ป่วย — `patient.note[]`, the patient's standing notes.
//
// This column is written by the LIVE BUBBLE APP too, so it is not a place to be
// creative: same fields, same semantics, and `popup` keeps meaning "surface this
// outside the page".
//
// ⚠️ THESE NOTES ARE NOT ONE-LINERS. Sampled live, two of three popup notes are
// multi-line running DEPOSIT LEDGERS kept over years ("…ฝากเพิ่ม 2000 = 8001 บาท").
// whitespace-pre-wrap is not styling here, it is the readability of the field.
//
// The patient object comes from PatientLayout's outlet context and is SHARED with
// every other patient sub-page, so writes merge into it locally — see mergeNote()
// for why assigning the endpoint's response would blank the header.

export default function PatientNotes({ patient, setPatient, me }) {
  const [adding, setAdding] = useState(false)
  const [draft, setDraft] = useState({ details: '', popup: false })
  const [editId, setEditId] = useState(null)
  const [editDraft, setEditDraft] = useState({ details: '', popup: false })
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [confirmDel, setConfirmDel] = useState(null)
  const toast = useToast()

  const notes = patientNotes(patient)
  const pid = patient?.id

  // Every write goes through here. `optimistic` is applied only after the server
  // agrees, so a 403/404 (someone else's note, or a uid that went stale) never
  // leaves the UI showing a change that did not happen.
  async function write(action, body, optimistic) {
    if (!pid) return false
    setBusy(true); setErr('')
    try {
      await postJson('/api/patients/note', { patient_id: pid, action, ...body })
      setPatient?.((p) => (p ? { ...p, note: mergeNote(p.note, action, { ...optimistic, me }) } : p))
      toast.success(action === 'delete' ? 'ลบโน้ตแล้ว' : 'บันทึกแล้ว')
      return true
    } catch (e) {
      setErr(apiErrorText(e, 'บันทึกไม่สำเร็จ'))
      toast.error(action === 'delete' ? 'ลบโน้ตไม่สำเร็จ' : 'บันทึกไม่สำเร็จ', { detail: apiErrorText(e, '') || undefined })
      return false
    } finally { setBusy(false) }
  }

  async function add() {
    const details = draft.details.trim()
    if (!details) return
    if (await write('add', { details, popup: draft.popup }, { details, popup: draft.popup })) {
      setDraft({ details: '', popup: false }); setAdding(false)
      // Re-read so the new note picks up its server-minted unique_id (and the
      // author `code` addon). Without this it stays unaddressable — no edit, no
      // delete, no pin — until a full browser reload.
      try {
        const fresh = await apiGet(`/api/patients/${pid}`)
        if (fresh?.id) setPatient?.(fresh)
      } catch { /* the optimistic entry still shows; it just isn't editable yet */ }
    }
  }

  function startEdit(key, n) {
    setEditDraft({ details: n.details || '', popup: n.popup === true })
    setEditId(key)
    setAdding(false)   // else two autoFocus textareas mount at once
    setErr('')
  }

  async function saveEdit(n) {
    const details = editDraft.details.trim()
    const popup = editDraft.popup === true
    if (!details) return
    if (await write('edit', { unique_id: noteId(n), details, popup }, { unique_id: noteId(n), details, popup })) {
      setEditId(null)
    }
  }

  // The two directions are NOT the same permission. Pinning ON writes a new
  // interruption into a colleague's note, so it is an `edit` — your own only.
  // Turning one OFF is `unpin`, which any author's note allows and which sends no
  // `details` at all: the proxy re-uses the stored text, so silencing an alarm
  // cannot change what was written. See PatientNoteAlert for the reasoning.
  async function togglePin(n) {
    const uid = noteId(n)
    if (n.popup) {
      await write('unpin', { unique_id: uid }, { unique_id: uid })
      return
    }
    await write('edit', { unique_id: uid, details: n.details, popup: true },
      { unique_id: uid, details: n.details, popup: true })
  }

  async function del(n) {
    // Close either way: ConfirmDialog is a fixed inset-0 overlay, so leaving it up
    // on failure hides the very error message explaining what went wrong.
    await write('delete', { unique_id: noteId(n) }, { unique_id: noteId(n) })
    setConfirmDel(null)
  }

  return (
    <section className="rounded-2xl border border-slate-200/80 bg-white p-4 sm:p-5 mb-6">
      <div className="flex items-center gap-2 mb-3">
        <Pin size={15} className="text-slate-400" strokeWidth={2} />
        <h3 className="text-[14.5px] font-semibold text-slate-900">โน้ตผู้ป่วย</h3>
        <CountBadge n={notes.length} />
        {!adding && (
          <Button variant="soft" size="sm" icon={Plus} onClick={() => setAdding(true)} className="ml-auto">เพิ่มโน้ต</Button>
        )}
      </div>

      {err && <ErrorState title="บันทึกไม่สำเร็จ" message={err} className="mb-3" />}

      {adding && (
        <NoteEditor
          value={draft}
          busy={busy}
          onChange={setDraft}
          onSave={add}
          onCancel={() => { setAdding(false); setDraft({ details: '', popup: false }); setErr('') }}
        />
      )}

      {patient == null ? (
        <div className="py-3"><Loading variant="inline" /></div>
      ) : notes.length === 0 && !adding ? (
        // No action here: the header's เพิ่มโน้ต is already on screen whenever this shows.
        <EmptyState variant="compact" title="ยังไม่มีโน้ตของคนไข้รายนี้" />
      ) : (
        <div className="space-y-2">
          {notes.map((n, i) => (
            <NoteRow
              key={noteId(n) || `t${n.timestamp}-${i}`}
              note={n}
              me={me}
              busy={busy}
              editing={editId === (noteId(n) || `t${n.timestamp}-${i}`)}
              draft={editDraft}
              onDraft={setEditDraft}
              onEdit={() => startEdit(noteId(n) || `t${n.timestamp}-${i}`, n)}
              onCancelEdit={() => { setEditId(null); setErr('') }}
              onSave={() => saveEdit(n)}
              onTogglePin={() => togglePin(n)}
              onDelete={() => setConfirmDel(n)}
            />
          ))}
        </div>
      )}

      {confirmDel && (
        <ConfirmDialog
          tone="danger"
          title="ลบโน้ตนี้?"
          message={`“${firstLine(confirmDel.details)}” จะถูกลบออกจากโน้ตผู้ป่วย`}
          confirmLabel="ลบโน้ต"
          busy={busy}
          onConfirm={() => del(confirmDel)}
          onCancel={() => setConfirmDel(null)}
        />
      )}
    </section>
  )
}

/* -------------------------------------------------------------------------- */

// The edit draft is owned by the PARENT. Keeping it here as useState looked fine
// and was not: the row's key is the note's stable unique_id, so this component
// instance outlives every write, and the seed values would go stale the moment the
// note changed underneath it (pin → edit → silently un-pinned).
function NoteRow({ note, me, busy, editing, draft, onDraft, onEdit, onCancelEdit, onSave, onTogglePin, onDelete }) {
  const mine = canEditNote(note, me)
  // A note with no uid cannot be addressed upstream — edit/delete match on
  // unique_id, and a blank one would resolve to |first, i.e. somebody ELSE's note.
  const addressable = !!noteId(note)
  const editable = mine && addressable
  // …but เลิกปักหมุด is open to everyone: a stale full-screen alarm is read by
  // colleagues, not by its author, who may well have left.
  const unpinnable = addressable && canUnpinNote(note, me)

  if (editing) {
    return (
      <NoteEditor value={draft} busy={busy} onChange={onDraft} onSave={onSave} onCancel={onCancelEdit} />
    )
  }

  return (
    <div className={`group rounded-xl border px-3 py-2.5 ${note.popup ? 'border-amber-200 bg-amber-50/50' : 'border-slate-100 bg-slate-50/40'}`}>
      <div className="flex items-start gap-2">
        {note.popup && <Pin size={13} className="text-amber-600 mt-0.5 shrink-0" strokeWidth={2.5} />}
        {/* pre-wrap is load-bearing: these are multi-line running ledgers. */}
        <p className="flex-1 min-w-0 text-[13.5px] text-slate-800 whitespace-pre-wrap break-words leading-relaxed">
          {note.details || <span className="text-slate-300">— (ไม่มีข้อความ)</span>}
        </p>
        <span className="shrink-0 flex items-center gap-0.5 transition sm:opacity-0 sm:pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto focus-within:opacity-100 focus-within:pointer-events-auto">
          {/* disabledReason: a disabled button gets no hover, so the kit puts the deny
              reason on a wrapper for us. */}
          <IconButton
            size="sm"
            icon={note.popup ? PinOff : Pin}
            label={note.popup ? 'เลิกปักหมุด — จะไม่เตือนเต็มจออีก (ข้อความโน้ตยังอยู่)' : 'ปักหมุด — ขึ้นหัวคนไข้ทุกหน้า + เตือนเต็มจอเมื่อเปิดคนไข้'}
            disabled={(note.popup ? !unpinnable : !editable) || busy}
            disabledReason={(note.popup ? !unpinnable : !editable) ? (note.popup ? 'โน้ตนี้ไม่มีรหัสอ้างอิง จึงเลิกปักหมุดไม่ได้' : pinDenyTitle(mine, addressable)) : undefined}
            onClick={onTogglePin}
          />
          <IconButton size="sm" icon={Pencil} label="แก้ไข" disabled={!editable || busy} disabledReason={!editable ? pinDenyTitle(mine, addressable) : undefined} onClick={onEdit} />
          <IconButton size="sm" icon={Trash2} label="ลบ" variant="danger-soft" disabled={!editable || busy} disabledReason={!editable ? pinDenyTitle(mine, addressable) : undefined} onClick={onDelete} />
        </span>
      </div>
      <div className="mt-1.5 flex items-center gap-1.5 text-[11.5px] text-slate-400">
        <span className="w-4 h-4 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center text-[9px] font-semibold shrink-0">
          {note.code ? nameInitial(note.code) : '·'}
        </span>
        <span className="font-medium text-slate-500">{noteAuthor(note)}</span>
        <span className="tabular-nums">· {fmtDateTime(note.timestamp)}</span>
        {noteEdited(note) && <span>· แก้ไข {fmtDate(note.modified_date)}</span>}
      </div>
    </div>
  )
}

const pinDenyTitle = (mine, addressable) =>
  !mine ? 'แก้ไข/ลบได้เฉพาะโน้ตที่คุณเขียนเอง'
        : !addressable ? 'โน้ตนี้ไม่มีรหัสอ้างอิง จึงแก้ไขไม่ได้'
        : ''

function NoteEditor({ value, busy, onChange, onSave, onCancel }) {
  return (
    <div className="rounded-xl border border-brand-200 bg-white p-3 mb-2">
      <Textarea
        autoFocus
        rows={3}
        value={value.details}
        onChange={(e) => onChange({ ...value, details: e.target.value })}
        onKeyDown={(e) => { if (e.key === 'Escape') onCancel() }}
        placeholder="เช่น คนไข้มียอดฝาก 6,000 บาท · ชอบหมอ OAT · เสี่ยงโดนคอมเพลนหากหมอเลทเวลา"
        className="resize-y"
      />
      <div className="mt-2 flex items-center gap-3 flex-wrap">
        {/* States the COST, not just the effect. A pinned note now also opens a
            full-screen เตือน the first time anyone opens this patient in a
            session — if everything is pinned, nothing is, and the person
            ticking the box is the only one who can prevent that. */}
        <Checkbox
          checked={value.popup === true}
          onChange={(v) => onChange({ ...value, popup: v })}
          label={(
            <span className="inline-flex items-center gap-1.5">
              <Pin size={12} className="text-amber-500 shrink-0" strokeWidth={2.5} />
              ปักหมุด — ขึ้นหัวคนไข้ทุกหน้า + เตือนเต็มจอเมื่อเปิดคนไข้
            </span>
          )}
        />
        <span className="ml-auto flex items-center gap-2">
          <Button variant="secondary" size="sm" onClick={onCancel} disabled={busy}>ยกเลิก</Button>
          <Button size="sm" icon={Check} onClick={onSave} loading={busy} disabled={!value.details.trim()}>บันทึก</Button>
        </span>
      </div>
    </div>
  )
}

// ConfirmDialog has no pre-wrap, so identify the note by its first line rather
// than pouring a multi-line ledger into a single collapsed paragraph.
function firstLine(details) {
  const l = String(details || '').split('\n').map((x) => x.trim()).find(Boolean) || ''
  return l.length > 120 ? `${l.slice(0, 120)}…` : (l || '(ไม่มีข้อความ)')
}

